/**
 * Группировка метрик по осям Y
 * Метрики из одной группы отображаются на одной оси
 * 
 * Группа 1: Регистрации (количество людей)
 * Группа 2: Заказы (количество заказов/платежей)
 * Группа 3: Участники вебинара (количество участников)
 * Группа 4: Сумма заказов (суммы в рублях)
 * Группа 5: Средние суммы (средние значения)
 * Группа 6: Конверсии (проценты)
 * Группа 7: Цена метрик (стоимость привлечения)
 * Группа 8: CPC/CPM (рекламные показатели)
 * Группа 9: Клики/Показы (рекламный трафик)
 * Группа 10: ROMI/ДРР (рентабельность)
 * Группа 11: ROAS (окупаемость)
 */

import { isCompareMetricKey, toBaseMetricKey } from '../../../constants/compare';

// Типы для групп осей
export interface AxisGroup {
    id: number;
    name: string;
    description: string;
    color: string;
    position: 'left' | 'right';
    offset?: number;
    formatter?: (value: number) => string;
}

// Определение групп осей
export const AXIS_GROUPS: AxisGroup[] = [
    {
        id: 1,
        name: 'Регистрации',
        description: 'Количество людей кто зарегистрировался',
        color: '#339dff',
        position: 'left',
    },
    {
        id: 2,
        name: 'Заказы',
        description: 'Количество заказов или платежей',
        color: '#00b379',
        position: 'left',
        offset: 60,
    },
    {
        id: 3,
        name: 'Участники вебинара',
        description: 'Количество участников вебинара',
        color: '#9c27b0',
        position: 'left',
        offset: 120,
    },
    {
        id: 4,
        name: 'Сумма заказов',
        description: 'Суммы заказов и платежей',
        color: '#ffc233',
        position: 'right',
        formatter: (value: number) => `${(value / 1000).toFixed(0)}K`,
    },
    {
        id: 5,
        name: 'Ср. сумма',
        description: 'Средние суммы заказов и платежей',
        color: '#ff9800',
        position: 'right',
        offset: 60,
    },
    {
        id: 6,
        name: 'Конверсии',
        description: 'Проценты и конверсии',
        color: '#d52f40',
        position: 'right',
        offset: 120,
        formatter: (value: number) => `${value}%`,
    },
    {
        id: 7,
        name: 'Цена метрик',
        description: 'Стоимость привлечения',
        color: '#5d3faa',
        position: 'right',
        offset: 180,
    },
    {
        id: 8,
        name: 'CPC/CPM',
        description: 'Рекламные показатели',
        color: '#607d8b',
        position: 'left',
        offset: 180,
    },
    {
        id: 9,
        name: 'Клики/Показы',
        description: 'Рекламный трафик',
        color: '#4ecdc4',
        position: 'left',
        offset: 240,
    },
    {
        id: 10,
        name: 'ROMI/ДРР',
        description: 'Рентабельность',
        color: '#e91e63',
        position: 'right',
        offset: 240,
        formatter: (value: number) => `${value}%`,
    },
    {
        id: 11,
        name: 'ROAS',
        description: 'Окупаемость рекламы',
        color: '#795548',
        position: 'right',
        offset: 300,
        formatter: (value: number) => `${value}%`,
    },
];

// Маппинг dataKey метрик на группы осей
export const METRIC_TO_AXIS_GROUP: Record<string, number> = {
    // Группа 1: Регистрации
    'regs_count': 1,
    'users_count': 1,
    'old_users_count': 1,
    'new_users_count': 1,
    'paid_regs_count': 1,
    'free_regs_count': 1,
    'no_app_count': 1,
    'no_app_users_count': 1,
    'page_views_count': 1,
    'page_visitors_count': 1,
    
    // Группа 2: Заказы
    'orders_count': 2,
    'forders_count': 2,
    'payments_count': 2,
    'orders_creators_count': 2,
    'buyers_count': 2,
    'refunds_count': 2,
    'buyers_with_refund_count': 2,
    'porders_count': 2,
    'worders_count': 2,
    'aorders_count': 2,
    
    // Группа 3: Участники вебинара
    'web_users_count': 3,
    'web_1min_users_count': 3,
    'web_5min_users_count': 3,
    'web_15min_users_count': 3,
    'web_30min_users_count': 3,
    'web_60min_users_count': 3,
    'web_30min_g_users_count': 3,
    'web_60min_g_users_count': 3,
    'web_120min_g_users_count': 3,
    'web_180min_g_users_count': 3,
    'web_240min_g_users_count': 3,
    'web_button_users_count': 3,
    'web_clickers_count': 3,
    
    // Группа 4: Сумма заказов
    'orders_sum': 4,
    'forders_sum': 4,
    'payments_sum': 4,
    'refunds_sum': 4,
    'payments_sum_with_commission': 4,
    'payments_sum_partial': 4,
    'payments_commission_sum': 4,
    'ad_costs': 4,
    'ads_spend': 4,
    'ads_spend_cabinet': 4,
    'profit': 4,
    'porders_sum': 4,
    'worders_sum': 4,
    'aorders_sum': 4,
    'payments_sum_partial_remain': 4,
    
    // Группа 5: Средние суммы
    'orders_sum_avg': 5,
    'orders_sum_avg_user': 5,
    'payments_sum_avg': 5,
    'payments_sum_per_reg': 5,
    'profit_per_reg': 5,
    'profit_per_user': 5,
    'payments_sum_per_user': 5,
    
    // Группа 6: Конверсии (%)
    'paid_regs_part': 6,
    'free_regs_part': 6,
    'new_users_part': 6,
    'user_to_creator_converse': 6,
    'orders_to_forders_converse': 6,
    'creator_to_buyer_converse': 6,
    'user_to_buyer_converse': 6,
    'orders_to_payments_converse': 6,
    'web_1min_users_part': 6,
    'web_5min_users_part': 6,
    'web_15min_users_part': 6,
    'web_30min_users_part': 6,
    'web_60min_users_part': 6,
    'web_30min_g_users_part': 6,
    'web_60min_g_users_part': 6,
    'web_120min_g_users_part': 6,
    'web_180min_g_users_part': 6,
    'web_240min_g_users_part': 6,
    'web_button_converse': 6,
    'web_button_to_creator_converse': 6,
    'user_to_web_converse': 6,
    'web_to_creator_converse': 6,
    'web_user_to_clicker_converse': 6,
    'clicker_to_creator_converse': 6,
    'user_to_web_user_converse': 6,
    'web_user_to_creator_converse': 6,
    'web_user_to_no_app_converse': 6,
    'user_to_no_app_converse': 6,
    'user_to_no_step1_converse': 6,
    'no_step1_to_no_step2_converse': 6,
    'no_step2_to_no_step3_converse': 6,
    'no_step3_to_no_step4_converse': 6,
    'no_step4_to_no_step5_converse': 6,
    'no_step5_to_no_app_converse': 6,
    'ctr': 6,
    'page_cr': 6,
    'user_to_refunder_converse': 6,
    'buyer_to_refunder_converse': 6,
    'web_to_app_converse': 6,
    'click_to_reg_converse': 6,
    'visitor_to_user_converse': 6,
    'ads_click_to_reg_converse': 6,
    'visit_to_reg_converse': 6,
    
    // Группа 7: Цена метрик
    'reg_price': 7,
    'user_price': 7,
    'orders_price': 7,
    'orders_creators_price': 7,
    'forders_price': 7,
    'payments_price': 7,
    'buyers_price': 7,
    'web_user_price': 7,
    
    // Группа 8: CPC/CPM
    'cpc': 8,
    'cpm': 8,
    
    // Группа 9: Клики/Показы
    'clicks': 9,
    'impressions': 9,
    'ad_clicks': 9,
    'ad_visitors': 9,
    'ads_clicks_count': 9,
    'ads_impressions': 9,
    
    // Группа 10: ROMI/ДРР
    'romi': 10,
    'drr': 10,
    
    // Группа 11: ROAS
    'roas': 11,
};

// Маппинг названий метрик на группы (для случаев когда dataKey недоступен)
export const METRIC_NAME_TO_AXIS_GROUP: Record<string, number> = {
    // Группа 1: Регистрации
    'Регистрации': 1,
    'Пользователи': 1,
    'Старые пользователи': 1,
    'Новые пользователи': 1,
    'Платные регистрации': 1,
    'Бесплатные регистрации': 1,
    'Заявки': 1,
    'Создатели заявок': 1,
    'Просмотры страниц после рег.': 1,
    'Посетители страниц после рег.': 1,
    
    // Группа 2: Заказы
    'Заказы': 2,
    'Заказы [Завершён]': 2,
    'Платежи': 2,
    'Создатели заказов': 2,
    'Покупатели': 2,
    'Возвраты': 2,
    'Оформившие возврат': 2,
    'Заказы [Частично оплачен]': 2,
    'Заказы [Ожидаем оплаты]': 2,
    'Актуальные заказы': 2,
    
    // Группа 3: Участники вебинара
    'Участники вебинара': 3,
    'Был на веб. < 1 мин.': 3,
    'Был на веб. < 5 мин.': 3,
    'Был на веб. < 15 мин.': 3,
    'Был на веб. < 30 мин.': 3,
    'Был на веб. < 1 час.': 3,
    'Был на веб. > 30 мин.': 3,
    'Был на веб. > 1 час.': 3,
    'Был на веб. > 2 час.': 3,
    'Был на веб. > 3 час.': 3,
    'Был на веб. > 4 час.': 3,
    'Нажал кнопку на веб.': 3,
    
    // Группа 4: Сумма заказов
    'Сумма заказов': 4,
    'Сумма заказов [Завершён]': 4,
    'Сумма платежей': 4,
    'Сумма возвратов': 4,
    'Сумма платежей с комиссией платёжных систем': 4,
    'Сумма платежей с комис. плат. сист. по частично оплаченным заказам.': 4,
    'Комиссия платёжных систем': 4,
    'Рекламные расходы': 4,
    'Прибыль': 4,
    'Сумма заказов [Частично оплачен]': 4,
    'Сумма заказов [Ожидаем оплаты]': 4,
    'Сумма актуальных заказов': 4,
    'Ожидаемая сумма платежей': 4,
    
    // Группа 5: Средние суммы
    'Ср. сумма заказа': 5,
    'Ср. сумма заказов на 1 польз.': 5,
    'Ср. сумма заказов на 1 пользователя': 5,
    'Средний чек платежа': 5,
    'Ср. чек платежа': 5,
    'Сумма платежей на 1 рег.': 5,
    'Прибыль на 1 рег.': 5,
    'Прибыль на 1 пользователя': 5,
    'Сумма платежей на 1 польз.': 5,
    
    // Группа 6: Конверсии (%)
    'Доля платных регистраций': 6,
    'Доля бесплатных регистраций': 6,
    'Доля новых пользователей': 6,
    'CR в создателя заказа': 6,
    'CR заказа в завершенный заказ': 6,
    'CR из созд. заказа в покупателя': 6,
    'CR польз. в покупателя': 6,
    'CR из польз. в покупателя': 6,
    'CR из суммы заказов в сумму платежей': 6,
    'Доля кто был на веб. < 1 мин.': 6,
    'Доля кто был на веб. < 5 мин.': 6,
    'Доля кто был на веб. < 15 мин.': 6,
    'Доля кто был на веб. < 30 мин.': 6,
    'Доля кто был на веб. < 1 час.': 6,
    'Доля кто был на веб. > 30 мин.': 6,
    'Доля кто был на веб. > 1 час.': 6,
    'Доля кто был на веб. > 2 час.': 6,
    'Доля кто был на веб. > 3 час.': 6,
    'Доля кто был на веб. > 4 час.': 6,
    'CR из участника в нажав. кнопку': 6,
    'CR из нажав. кнопку в создат. заказа': 6,
    'CR из пользователя в участника веб.': 6,
    'CR из участника веб. в создат. заказа': 6,
    'CTR': 6,
    'CR страницы': 6,
    'CR из пользователей в оформившего возврат': 6,
    'CR из покупателя в оформившего возврат': 6,
    'CR из участника веб. в заявку': 6,
    'CR из перехода в регистрацию': 6,
    'CR из посетителей в пользователей': 6,
    
    // Группа 7: Цена метрик
    'Цена регистрации': 7,
    'Цена пользователя': 7,
    'Цена заказа': 7,
    'Цена создателя заказа': 7,
    'Цена заказа [Завершён]': 7,
    'Цена платежа': 7,
    'Цена покупателя': 7,
    'Цена участника вебинара': 7,
    
    // Группа 8: CPC/CPM
    'CPC': 8,
    'CPM': 8,
    
    // Группа 9: Клики/Показы
    'Клики': 9,
    'Показы': 9,
    'Переход с рекламы': 9,
    'Посетители рекламируемой страницы': 9,
    
    // Группа 10: ROMI/ДРР
    'ROMI': 10,
    'ДРР': 10,
    
    // Группа 11: ROAS
    'ROAS': 11,
};

/**
 * Удалить суффикс отчёта из dataKey (_2, _3, _4, _5 и т.д.)
 * Например: 'orders_count_2' -> 'orders_count'
 */
const removeReportSuffix = (dataKey: string): string => {
    // Паттерн для суффиксов отчётов: _2, _3, _4, _5 в конце строки
    return dataKey.replace(/_[2-9]$/, '');
};

/**
 * Удалить суффикс _cumulative из dataKey
 * Например: 'orders_count_cumulative' -> 'orders_count'
 *           'orders_count_2_cumulative' -> 'orders_count_2'
 */
const removeCumulativeSuffix = (dataKey: string): string => {
    return dataKey.replace(/_cumulative$/, '');
};

/**
 * Получить группу оси для метрики по dataKey или названию
 */
export const getAxisGroupForMetric = (dataKeyOrName: string): number => {
    // Сначала проверяем по точному dataKey
    if (METRIC_TO_AXIS_GROUP[dataKeyOrName]) {
        return METRIC_TO_AXIS_GROUP[dataKeyOrName];
    }

    // Метрика второго периода сравнения — та же ось, что и у основной метрики
    if (isCompareMetricKey(dataKeyOrName)) {
        return getAxisGroupForMetric(toBaseMetricKey(dataKeyOrName));
    }

    // Пробуем без суффикса _cumulative (накопительный график должен быть на той же оси)
    const withoutCumulative = removeCumulativeSuffix(dataKeyOrName);
    if (withoutCumulative !== dataKeyOrName) {
        // Это накопительная метрика — рекурсивно определяем группу базовой метрики
        return getAxisGroupForMetric(withoutCumulative);
    }
    
    // Пробуем без суффикса отчёта (_2, _3, _4, _5)
    const baseDataKey = removeReportSuffix(dataKeyOrName);
    if (METRIC_TO_AXIS_GROUP[baseDataKey]) {
        return METRIC_TO_AXIS_GROUP[baseDataKey];
    }
    
    // Затем по названию
    if (METRIC_NAME_TO_AXIS_GROUP[dataKeyOrName]) {
        return METRIC_NAME_TO_AXIS_GROUP[dataKeyOrName];
    }
    
    // По умолчанию группа 1
    return 1;
};

/**
 * Получить конфигурацию группы оси по ID
 */
export const getAxisGroupById = (groupId: number): AxisGroup | undefined => {
    return AXIS_GROUPS.find(g => g.id === groupId);
};

/**
 * Получить уникальные группы осей для списка метрик
 */
export const getUniqueAxisGroups = (metrics: Array<{ dataKey?: string; name?: string }>): AxisGroup[] => {
    const groupIds = new Set<number>();
    
    metrics.forEach(metric => {
        const groupId = getAxisGroupForMetric(metric.dataKey || metric.name || '');
        groupIds.add(groupId);
    });
    
    return Array.from(groupIds)
        .map(id => getAxisGroupById(id))
        .filter((g): g is AxisGroup => g !== undefined)
        .sort((a, b) => a.id - b.id);
};

/**
 * Цвета для осей по группам
 */
export const AXIS_GROUP_COLORS: Record<number, string> = {
    1: '#339dff',  // Регистрации - синий
    2: '#00b379',  // Заказы - зеленый
    3: '#9c27b0',  // Участники вебинара - фиолетовый
    4: '#ffc233',  // Сумма заказов - желтый
    5: '#ff9800',  // Средние суммы - оранжевый
    6: '#d52f40',  // Конверсии - красный
    7: '#5d3faa',  // Цена метрик - темно-фиолетовый
    8: '#607d8b',  // CPC/CPM - серый
    9: '#4ecdc4',  // Клики/Показы - бирюзовый
    10: '#e91e63', // ROMI/ДРР - розовый
    11: '#795548', // ROAS - коричневый
};

/**
 * Получить цвет для метрики на основе её группы
 */
export const getColorForMetric = (dataKeyOrName: string, index: number): string => {
    const groupId = getAxisGroupForMetric(dataKeyOrName);
    const baseColor = AXIS_GROUP_COLORS[groupId] || '#339dff';
    
    // Если несколько метрик в одной группе, делаем небольшую вариацию цвета
    if (index > 0) {
        return adjustColorBrightness(baseColor, index * 15);
    }
    
    return baseColor;
};

/**
 * Вспомогательная функция для изменения яркости цвета
 */
const adjustColorBrightness = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

/**
 * Интерфейс для данных кастомной метрики
 */
export interface CustomMetricData {
    base_metric_name?: string;
    metric_formula?: string;
    name?: string;
    dataKey?: string;
    unit?: string;
}

/**
 * Получить группу оси для кастомной метрики
 * 
 * Логика определения группы:
 * 1. Если unit = '%' или 'Percentage' — группа 6 (Конверсии)
 * 2. Если есть metric_formula с calculate_conversion — группа 6 (Конверсии)
 * 3. Если есть base_metric_name — используем группу базовой метрики
 * 4. Если есть metric_formula:
 *    - multiply/* или divide/* с метрикой — используем группу метрики из формулы
 *    - multiply/* или divide/* с числом — независимая группа (возвращаем -1)
 *    - add/subtract с метрикой — используем группу метрики из формулы
 * 5. По умолчанию — группа 1
 * 
 * @param customMetric - данные кастомной метрики
 * @returns номер группы оси (1-11) или -1 для независимой группы
 */
export const getAxisGroupForCustomMetric = (customMetric: CustomMetricData): number => {
    // 1. Если unit = '%' или 'Percentage' — группа 6 (Конверсии)
    if (customMetric?.unit === '%' || customMetric?.unit === 'Percentage') {
        return 6;
    }
    
    // 2. Если есть metric_formula с calculate_conversion — группа 6 (Конверсии)
    if (customMetric?.metric_formula) {
        const formulaParts = customMetric.metric_formula.split('/');
        const operation = formulaParts[0];
        
        if (operation === 'calculate_conversion') {
            return 6;
        }
    }
    
    // 3. Если есть base_metric_name — используем группу базовой метрики
    if (customMetric?.base_metric_name) {
        const baseMetricName = customMetric.base_metric_name.replace(/(_\d+)?_cumulative$/g, '').replace(/(_\d+)$/g, '');
        const group = getAxisGroupForMetric(baseMetricName);
        return group;
    }
    
    // 4. Если есть metric_formula — анализируем формулу
    if (customMetric?.metric_formula) {
        const formulaParts = customMetric.metric_formula.split('/');
        const operation = formulaParts[0];
        
        // multiply или divide с числом — независимая группа
        // Формат: multiply/metric_name/number или divide/metric_name/number
        if (operation === 'multiply' || operation === 'divide') {
            // Проверяем, есть ли метрика в формуле (второй элемент)
            if (formulaParts.length >= 2 && formulaParts[1]) {
                const metricInFormula = formulaParts[1].replace(/(_\d+)?_cumulative$/g, '').replace(/(_\d+)$/g, '');
                // Если это метрика (не число), используем её группу
                if (isNaN(Number(metricInFormula)) && METRIC_TO_AXIS_GROUP[metricInFormula]) {
                    return getAxisGroupForMetric(metricInFormula);
                }
            }
            // Иначе возвращаем -1 (независимая группа)
            return -1;
        }
        
        // add или subtract — используем группу первой метрики из формулы
        if (operation === 'add' || operation === 'subtract') {
            if (formulaParts.length >= 2 && formulaParts[1]) {
                const metricInFormula = formulaParts[1].replace(/(_\d+)?_cumulative$/g, '').replace(/(_\d+)$/g, '');
                return getAxisGroupForMetric(metricInFormula);
            }
        }
        
        // Для других операций пробуем найти метрику в формуле
        for (let i = 1; i < formulaParts.length; i++) {
            const part = formulaParts[i];
            if (part && isNaN(Number(part))) {
                const cleanedPart = part.replace(/(_\d+)?_cumulative$/g, '').replace(/(_\d+)$/g, '');
                const group = METRIC_TO_AXIS_GROUP[cleanedPart];
                if (group) {
                    return group;
                }
            }
        }
    }
    
    // 5. По умолчанию — группа 1
    return 1;
};

/**
 * Расширенная функция получения группы оси для метрики
 * Поддерживает как обычные метрики, так и кастомные
 * 
 * @param dataKeyOrName - dataKey или название метрики
 * @param customMetricData - опциональные данные кастомной метрики (base_metric_name, metric_formula)
 * @returns номер группы оси (1-11) или -1 для независимой группы кастомных метрик
 */
export const getAxisGroupForMetricExtended = (
    dataKeyOrName: string,
    customMetricData?: CustomMetricData
): number => {
    // Проверяем, является ли это кастомной метрикой (начинается с _)
    if (dataKeyOrName.startsWith('_') && customMetricData) {
        return getAxisGroupForCustomMetric(customMetricData);
    }
    
    // Для обычных метрик используем стандартную логику
    return getAxisGroupForMetric(dataKeyOrName);
};
